import { AuditAction, AuditEntityType, AuditModule } from '@trulyinbox/types';
import type { BadgeProps } from '@/components/ui/badge';
import { capitalize } from '@/lib/string';
import { DAY_LABELS } from '@/features/email-accounts/warmup-form';
import type { ListAuditLogsParams } from '@/lib/audit-logs';
import { PLAN_NAME_SNAPSHOT_KEYS, planNameLabel } from './plan-name-labels';
import type { AuditLogsSearch } from './search-schema';

// Non-component module (Fast-Refresh-clean): the audit-logs label/colour maps,
// the URL⇆draft adapters, the search→list-params projection, and the diff
// computation. Lives here (not in a `.tsx`) so the page/drawer/diff files export
// only React components (see apps/web CLAUDE.md).

type BadgeVariant = NonNullable<BadgeProps['variant']>;

/** Module → Badge tone. Falls back to `neutral` for an unmapped module. */
const AUDIT_MODULE_BADGE: Record<AuditModule, BadgeVariant> = {
  [AuditModule.MEMBERSHIP]: 'info',
  [AuditModule.BILLING]: 'brand',
  [AuditModule.WARMUP]: 'success',
  [AuditModule.MAILBOXES]: 'secondary',
  [AuditModule.TEMPLATES]: 'outline',
  [AuditModule.INTEGRATIONS]: 'neutral',
};

export function auditModuleBadgeVariant(module: string): BadgeVariant {
  return AUDIT_MODULE_BADGE[module as AuditModule] ?? 'neutral';
}

/**
 * Humanize an audit action code (`<domain>.<verb>`, e.g. `member.role_changed`)
 * into "Member · Role changed". Derived rather than a hand-maintained label map
 * so a newly-added `AuditAction` member renders without a second edit here.
 * Underscores become spaces on both sides (`api_key.revoked` → "Api key · Revoked").
 */
export function formatAuditAction(action: string): string {
  const [domain, ...rest] = action.split('.');
  const verb = rest.join('.').replace(/_/g, ' ');
  const domainLabel = capitalize((domain ?? '').replace(/_/g, ' '));
  return verb ? `${domainLabel} · ${capitalize(verb)}` : domainLabel;
}

/**
 * Humanize an entity-type code (`workspaceInvite` → "Workspace invite"). Derived
 * from the enum VALUE, not a hand-maintained label map, so a newly-added
 * `AuditEntityType` member renders without a second edit here.
 */
export function formatAuditEntityType(entityType: string): string {
  return humanizeKey(entityType);
}

/**
 * Project the validated URL search onto the API list params. Shared by the page
 * (`useAuditLogsQuery`) and the route loader (`loaderDeps`/`ensureQueryData`) so
 * both produce identical params — same request, same cache key. Inactive values
 * are already `undefined` on the search object (the Zod schema collapses them).
 */
export function searchToAuditParams(search: AuditLogsSearch): ListAuditLogsParams {
  return {
    module: search.module,
    action: search.action,
    actorUserId: search.actorUserId,
    entityType: search.entityType,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    page: search.page,
    limit: search.limit,
    sort: search.sort,
  };
}

/** Count of committed filters (excludes pagination + sort) — drives the toolbar
 * "Filters" button badge. */
export function activeAuditFilterCount(search: AuditLogsSearch): number {
  let n = 0;
  if (search.module?.length) n += 1;
  if (search.action?.length) n += 1;
  if (search.actorUserId !== undefined) n += 1;
  if (search.entityType) n += 1;
  if (search.dateFrom || search.dateTo) n += 1;
  return n;
}

// ── Filter drawer draft ──────────────────────────────────────────────────────

/** The drawer's local draft — a flat snapshot of every committed filter value.
 * Nothing here commits to the URL until Apply. */
export type AuditFilterDraft = {
  module: AuditModule[];
  action: AuditAction[];
  actorUserId?: number;
  entityType?: AuditEntityType;
  dateFrom?: string;
  dateTo?: string;
};

export function emptyAuditDraft(): AuditFilterDraft {
  return { module: [], action: [], actorUserId: undefined, entityType: undefined };
}

/** Seed the draft from the current URL search on drawer open. */
export function auditSearchToDraft(search: AuditLogsSearch): AuditFilterDraft {
  return {
    module: search.module ?? [],
    action: search.action ?? [],
    actorUserId: search.actorUserId,
    entityType: search.entityType,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
  };
}

/**
 * Project a draft back onto a search patch committed on Apply. Inactive values
 * collapse to `undefined` so they leave NO URL key. Every drawer-owned key is
 * present in the patch (value or `undefined`) so a cleared filter overwrites a
 * previously-set URL key.
 */
export function auditDraftToPatch(draft: AuditFilterDraft): Partial<AuditLogsSearch> {
  return {
    module: draft.module.length > 0 ? draft.module : undefined,
    action: draft.action.length > 0 ? draft.action : undefined,
    actorUserId: draft.actorUserId,
    entityType: draft.entityType,
    dateFrom: draft.dateFrom,
    dateTo: draft.dateTo,
  };
}

// ── Before → after diff ──────────────────────────────────────────────────────

/** One row of the before→after comparison. `changed` is true when the two
 * sides differ (drives the highlight). */
export type DiffRow = {
  /** Humanized for display — keep the raw key as `rawKey` if a future caller
   * needs it (e.g. for a `key`-based React key that survives a relabeling). */
  key: string;
  rawKey: string;
  before: string;
  after: string;
  changed: boolean;
};

/** "warmup paused" / "not-owner" → "Warmup Paused" / "Not Owner". */
function titleCaseWords(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => capitalize(word.toLowerCase()))
    .join(' ');
}

/**
 * A role code as shown to people: `editor` → "Editor", `not-owner` →
 * "Not Owner". Used for every role field in a diff/summary and for the
 * actor's role on the list and the detail dialog.
 */
export function formatAuditRole(role: string): string {
  return titleCaseWords(role);
}

/** Keys whose value is a role code (`role`, `newRole`, …). */
const ROLE_KEY = /role$/i;

/** An enum wire value (`WARMUP_PAUSED`): upper case with at least one `_`.
 * Single upper-case words are left alone so acronyms (`SPF`, `SMTP`) survive. */
const SCREAMING_SNAKE = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/;

/**
 * Shown wherever a value isn't there: a field the change cleared (`null`), a key
 * present on only one side of the diff, or a metadata key with no value. One
 * glyph for all three, deliberately — the trail's job is to say what changed,
 * and "nothing here" is the same fact to a reader however the backend spelled
 * it. Keep every absent-value render pointed at this constant so the diff table
 * and the create/delete summary can't drift to different dashes.
 */
const EMPTY_VALUE = '-';

/**
 * Render an arbitrary JSON value as a compact, single-cell string. `key` is
 * the field's raw name — a plan-name snapshot key (`planNameLabel`) is
 * resolved through the legacy plan-code map so an old audit row shows the
 * plan's current display name instead of the raw code that was baked into
 * it pre-migration (see `plan-name-labels.ts`). Role codes and enum values
 * (`WARMUP_PAUSED`) are title-cased.
 *
 * `== null` covers `undefined` alongside `null` on purpose. `null` is the one
 * that reaches a reader — the api writes it explicitly (`priorStatusById.get(id)
 * ?? null` on a bulk enable, `replyRate` when a custom rate is switched off, an
 * un-assigned `templateCollectionId`) and `changedFieldsOnly` copies it through
 * untouched, so this used to render the literal text "null" in the Before/After
 * cell. `undefined` can't survive JSON transport, but it arrives from a hand-built
 * object in a test or a future caller, and `JSON.stringify(undefined)` returns
 * `undefined` rather than a string — which the `: string` return type would have
 * quietly lied about.
 */
function formatDiffValue(key: string, value: unknown): string {
  if (value == null) return EMPTY_VALUE;
  if (typeof value === 'string') {
    if (PLAN_NAME_SNAPSHOT_KEYS.has(key)) return planNameLabel(value);
    if (ROLE_KEY.test(key)) return formatAuditRole(value);
    if (SCREAMING_SNAKE.test(value)) return titleCaseWords(value);
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // A function or symbol also stringifies to `undefined`; unreachable from a JSON
  // payload, but the fallback keeps the declared return type true in every case.
  return JSON.stringify(value) ?? EMPTY_VALUE;
}

/**
 * Whether an entry is a genuine field-level update. The backend sets both
 * `before` and `after` only for real updates (role/quota/subscription/warmup/
 * trial changes); create/delete/remove actions null both sides and carry their
 * detail in `metadata` instead — so presence of both is the reliable signal.
 */
export function isUpdateEntry(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): boolean {
  return before != null && after != null;
}

/**
 * Short label for the entry's change kind — `Updated` for genuine updates, else
 * `Removed` for delete/remove/revoke/unlink actions and `Created` for the rest. Derived
 * from before/after presence first, falling back to the action verb.
 */
export function auditChangeKind(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  action: string,
): 'Updated' | 'Created' | 'Removed' {
  if (isUpdateEntry(before, after)) return 'Updated';
  return /delet|remov|revok|unlink/i.test(action) ? 'Removed' : 'Created';
}

// Internal bookkeeping keys folded into `metadata` by the audit writer — noise
// in the human-facing summary, so they're dropped from it.
const INTERNAL_METADATA_KEYS = new Set(['source', 'batchId']);

/** One line of the create/delete summary — a humanized key + rendered value. */
export type SummaryRow = { key: string; value: string };

/** Humanize a metadata key (`newRole` / `trial_days` → "New role" / "Trial days"). */
function humanizeKey(key: string): string {
  const spaced = key.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return capitalize(spaced.toLowerCase());
}

/**
 * Build the readable key/value summary shown for create/delete entries (which
 * have no before/after). Internal keys are dropped; values reuse the same
 * compact renderer as the diff table.
 */
export function metadataSummary(metadata: Record<string, unknown> | null): SummaryRow[] {
  if (!metadata) return [];
  return Object.entries(metadata)
    .filter(([key]) => !INTERNAL_METADATA_KEYS.has(key))
    .map(([key, value]) => ({ key: humanizeKey(key), value: formatDiffValue(key, value) }));
}

/** Warmup-settings snapshot key holding the weekly send schedule (one row per slot). */
const SCHEDULE_KEY = 'schedule';

type ScheduleSlot = {
  dayOfWeek: number;
  slotIndex: number;
  weight: number;
  enabled: boolean;
  startTime: string;
  endTime: string;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** "09:00:00" → "09:00". */
function trimSeconds(time: string): string {
  return time.replace(/^(\d{2}:\d{2}):\d{2}$/, '$1');
}

/** One day of a schedule: "Off", or its enabled slots as "09:00–12:00 (60%), …". */
function formatScheduleDay(slots: ScheduleSlot[]): string {
  const enabled = slots.filter((s) => s.enabled).sort((a, b) => a.slotIndex - b.slotIndex);
  if (enabled.length === 0) return 'Off';
  return enabled
    .map((s) => `${trimSeconds(s.startTime)}–${trimSeconds(s.endTime)} (${s.weight}%)`)
    .join(', ');
}

/**
 * Expand one top-level value into display leaves keyed by a stable path, so a nested
 * value never renders as a raw JSON blob: `schedule` becomes one leaf per weekday,
 * a plain object one leaf per field, a primitive array a comma list.
 */
function diffLeaves(key: string, value: unknown): Map<string, { label: string; text: string }> {
  const leaves = new Map<string, { label: string; text: string }>();
  const label = humanizeKey(key);
  if (key === SCHEDULE_KEY && (value == null || Array.isArray(value))) {
    const slots = (value ?? []) as ScheduleSlot[];
    DAY_LABELS.forEach((day, dayOfWeek) => {
      leaves.set(`${key}.${dayOfWeek}`, {
        label: `${label} · ${day}`,
        text: formatScheduleDay(slots.filter((s) => s.dayOfWeek === dayOfWeek)),
      });
    });
  } else if (isPlainObject(value)) {
    for (const [child, childValue] of Object.entries(value)) {
      for (const [path, leaf] of diffLeaves(child, childValue)) {
        leaves.set(`${key}.${path}`, { label: `${label} · ${leaf.label}`, text: leaf.text });
      }
    }
    if (leaves.size === 0) leaves.set(key, { label, text: 'None' });
  } else if (Array.isArray(value) && value.every((v) => v === null || typeof v !== 'object')) {
    leaves.set(key, { label, text: value.length ? value.map(String).join(', ') : 'None' });
  } else {
    leaves.set(key, { label, text: formatDiffValue(key, value) });
  }
  return leaves;
}

/**
 * Compute the key/value diff between two `before`/`after` JSON blobs. Rows follow the
 * sorted union of top-level keys; a key absent from one side renders `EMPTY_VALUE` there and is
 * marked changed. Nested values expand via `diffLeaves`, and only their changed leaves
 * are kept (e.g. just the schedule days that moved, not all seven).
 */
export function computeDiff(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): DiffRow[] {
  const keys = Array.from(
    new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]),
  ).sort();
  return keys.flatMap((key) => {
    const hasBefore = before != null && key in before;
    const hasAfter = after != null && key in after;
    const beforeLeaves = hasBefore ? diffLeaves(key, before[key]) : new Map();
    const afterLeaves = hasAfter ? diffLeaves(key, after[key]) : new Map();
    const paths = Array.from(new Set([...beforeLeaves.keys(), ...afterLeaves.keys()]));
    const nested = paths.length > 1 || (paths.length === 1 && paths[0] !== key);
    return paths
      .map((path) => {
        const b = beforeLeaves.get(path);
        const a = afterLeaves.get(path);
        return {
          key: (a ?? b)!.label,
          rawKey: path,
          before: b?.text ?? EMPTY_VALUE,
          after: a?.text ?? EMPTY_VALUE,
          changed: !hasBefore || !hasAfter || b?.text !== a?.text,
        };
      })
      .filter((row) => !nested || row.changed);
  });
}
